import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ProductImageCarousel } from '@/components/ui/ProductImageCarousel';
import { Product, PaymentLink } from '@/types/store';
import { PageSettings } from '@/types/editor';
import { ExternalLink, ShoppingCart, X } from 'lucide-react';
import { getButtonClass, getGlobalButtonStyle } from '@/lib/linkStyle';
import { useLanguage } from '@/contexts/LanguageContext';
import { pickByLanguage } from '@/lib/language';

/** Returns the effective list of purchase links for a product,
 *  merging `payment_links` (new) and the legacy `payment_link` field. */
export function getEffectiveLinks(product: Product): PaymentLink[] {
  const links: PaymentLink[] = (product.payment_links ?? []).filter((l) => l.url?.trim());
  // Backward compat: include legacy single link if not already present
  if (product.payment_link?.trim() && !links.some((l) => l.url === product.payment_link)) {
    links.push({ label: 'Comprar', url: product.payment_link });
  }
  return links;
}

interface ProductDetailSheetProps {
  product: Product | null;
  settings: PageSettings | null;
  open: boolean;
  onClose: () => void;
  onBuyClick: (product: Product, link: PaymentLink) => void;
  textColor?: string;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

export function ProductDetailSheet({
  product,
  settings,
  open,
  onClose,
  onBuyClick,
  textColor = '#1a1a2e',
}: ProductDetailSheetProps) {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });

  if (!product) return null;

  const images =
    product.images && product.images.length > 0
      ? product.images
      : product.image_url
      ? [product.image_url]
      : [];

  const links = getEffectiveLinks(product);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl p-0 max-h-[92dvh] overflow-y-auto focus:outline-none"
        style={{ fontFamily: settings?.font_family || 'Inter' }}
      >
        {/* Image */}
        {images.length > 0 ? (
          <ProductImageCarousel
            images={images}
            alt={product.name}
            className="w-full h-56 object-cover rounded-none"
            placeholderClassName="w-full h-56 bg-muted flex items-center justify-center"
            intervalMs={3500}
          />
        ) : (
          <div className="w-full h-40 bg-muted flex items-center justify-center">
            <ShoppingCart className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}

        <div className="p-5 space-y-4">
          <SheetHeader className="text-left space-y-1">
            <SheetTitle className="text-xl font-bold leading-tight">{product.name}</SheetTitle>
            <p className="text-2xl font-bold text-primary">
              {formatPrice(product.price, product.currency)}
            </p>
          </SheetHeader>

          {product.description && (
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap break-words">
              {product.description}
            </p>
          )}

          {/* Purchase links */}
          {links.length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('Onde comprar', 'Where to buy')}
              </p>
              {links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onBuyClick(product, link)}
                  className={`flex items-center justify-between w-full px-4 py-3 text-sm font-semibold transition-[background-color,border-color,opacity] hover:opacity-95 ${
                    settings ? getButtonClass(settings) : 'rounded-xl bg-primary text-primary-foreground'
                  }`}
                  style={settings ? getGlobalButtonStyle(settings) : {}}
                >
                  <span>{link.label || t('Comprar agora', 'Buy Now')}</span>
                  <ExternalLink className="h-4 w-4 shrink-0 ml-2 opacity-80" />
                </a>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
